import React from "react";
import { Compass, Clock, Sparkles, MapPin, Plane } from "lucide-react";
import { Button } from "../ui/button";
import { scoreClass, scoreHex, scoreHint } from "../../lib/scoreTone";

const LUNAMORE_PHONE = "(844) 458-6266";

const ringGradient = (s) => {
  if (s >= 90) return "conic-gradient(#C8A96E, #E6D3A8, #C8A96E)";
  if (s >= 75) return "conic-gradient(#9CBF8E, #C5DDBB, #9CBF8E)";
  return "conic-gradient(#4A3356, #6A4F78, #4A3356)";
};

export function ScoreOrb({ result }) {
  return (
    <>
      <div className="mt-16 flex items-center justify-center">
        <div className="relative">
          <div
            className="w-72 h-72 sm:w-96 sm:h-96 rounded-full orb-pulse relative"
            style={{ padding: 6, background: result ? ringGradient(result.score) : "conic-gradient(#4A3356, #1C1622, #4A3356)" }}
            data-testid="scoring-orb"
          >
            <div className="w-full h-full rounded-full bg-[#15111A] flex items-center justify-center flex-col relative overflow-hidden">
              <img src="https://images.unsplash.com/photo-1628498188904-036f5e25e93e?crop=entropy&cs=srgb&fm=jpg&q=85" alt="night sky" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              <div className="relative text-center">
                {result ? (
                  <>
                    <div className={`font-mono-scanner text-8xl ${scoreClass(result.score)}`} data-testid="scoring-orb-value">{result.score}</div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#A89C98] mt-2">{result.city}</div>
                  </>
                ) : (
                  <>
                    <Compass size={48} className="mx-auto text-[#C8A96E]" />
                    <div className="text-xs uppercase tracking-[0.3em] text-[#A89C98] mt-3">Awaiting Input</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {result && !result.easter_egg && (
        <div className="mt-8 text-center text-[#A89C98]" data-testid="scanner-hint">{scoreHint(result.score)}</div>
      )}
    </>
  );
}

export function ScarcityBanner({ scarcity }) {
  if (!scarcity) return null;
  return (
    <div
      className="mt-6 mx-auto max-w-3xl rounded-xl border p-4 flex items-start gap-3"
      style={{ borderColor: `${scarcity.color}55`, background: `linear-gradient(135deg, ${scarcity.color}10, transparent)` }}
      data-testid="scanner-scarcity"
    >
      <Clock size={18} style={{ color: scarcity.color }} className="mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: scarcity.color }} data-testid="scarcity-planet">
          {scarcity.planet} · Rare Transit · {scarcity.rating}/10
        </div>
        <p className="mt-1 text-sm text-white/90 leading-snug" data-testid="scarcity-text">{scarcity.text}</p>
      </div>
      <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A89C98] shrink-0">{scarcity.days_left}d left</span>
    </div>
  );
}

export function WildcardCard({ wildcard, onScan }) {
  if (!wildcard) return null;
  const color = wildcard.score >= 75 ? scoreHex(wildcard.score) : "#9E8BB0";
  return (
    <div
      className="mt-6 mx-auto max-w-3xl rounded-2xl border-2 border-[#C8A96E]/70 p-6 relative overflow-hidden"
      style={{ background: "radial-gradient(circle at 20% 20%, #C8A96E22 0%, transparent 60%), linear-gradient(135deg, #1C1622 0%, #15111A 100%)" }}
      data-testid="scanner-wildcard"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#C8A96E]/10 blur-3xl" />
      <div className="relative flex items-center gap-2">
        <Sparkles size={14} className="text-[#C8A96E]" />
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#C8A96E]">Wildcard Unlocked · Bonus Draw</span>
      </div>
      <div className="mt-3 flex items-baseline gap-4 flex-wrap">
        <h3 className="font-heading text-3xl sm:text-4xl text-white" data-testid="wildcard-city">{wildcard.city_display}</h3>
        {wildcard.country && <span className="text-sm text-[#A89C98]">{wildcard.country}</span>}
        <span className="font-mono-scanner text-3xl" style={{ color }} data-testid="wildcard-score">{wildcard.score}</span>
      </div>
      <p className="mt-2 text-sm text-white/80 max-w-2xl">
        A hidden city your chart is quietly pulling toward. This bonus draw unlocks after your 3rd scan.
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button data-testid="wildcard-scan-btn" onClick={() => onScan(wildcard.city_display)} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-10 px-5 uppercase tracking-widest text-xs font-bold">
          <MapPin size={14} className="mr-2" /> Scan {wildcard.city_display}
        </Button>
        <a data-testid="wildcard-booktrip-btn" href="tel:+18444586266" className="border border-[#C8A96E] text-[#C8A96E] hover:bg-[#C8A96E] hover:text-[#15111A] rounded-none h-10 px-5 uppercase tracking-widest text-xs font-bold flex items-center gap-2 transition-colors">
          Book Trip · (844) 458-6266
        </a>
      </div>
    </div>
  );
}

export function JourneyCTA({ city, onOpen, onPackages }) {
  return (
    <section
      className="mt-10 mx-auto max-w-3xl rounded-2xl border-2 border-[#C8A96E]/50 p-6 relative overflow-hidden"
      style={{ background: "radial-gradient(circle at 80% 20%, #4A335633 0%, transparent 60%), linear-gradient(135deg, #1C1622 0%, #15111A 100%)" }}
      data-testid="turn-map-into-journey"
    >
      <div className="absolute -bottom-10 -left-10 w-52 h-52 rounded-full bg-[#C8A96E]/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#C8A96E]" />
          <span className="text-[10px] uppercase tracking-[0.32em] text-[#C8A96E]">Next Step</span>
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl text-white mt-2 leading-tight" data-testid="turn-map-title">Turn Your Map Into Your Journey</h2>
        <p className="mt-3 text-sm text-white/80 max-w-2xl">
          A soul journey plan calibrated to your natal chart, your resonance in {city || "this city"} or other locations based on your intention or goal, and the way you want to travel — powered by Lunamore.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button data-testid="plan-wellness-trip-btn" onClick={onOpen} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-12 px-6 uppercase tracking-widest text-xs font-bold flex items-center gap-2">
            <MapPin size={14} /> Plan My Wellness Trip
          </Button>
          <button
            type="button"
            onClick={onPackages}
            data-testid="lunamore-packages-btn"
            className="inline-flex items-center gap-2 h-12 px-6 border border-[#C8A96E] text-[#C8A96E] hover:bg-[#C8A96E]/10 uppercase tracking-widest text-xs font-bold transition-colors"
          >
            <Plane size={14} /> Explore Aligned Travel Packages for {city || "this city"}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-[#A89C98]" data-testid="lunamore-positioning">
          Lunamore is a specialized astro-travel service: stays, healers and local experiences in {city || "this city"} curated around your birth-chart energy for that exact place — not a generic booking tool. Concierge {LUNAMORE_PHONE}.
        </p>
      </div>
    </section>
  );
}
